import "react-datepicker/dist/react-datepicker.css";

import { Grid, TextField } from "@material-ui/core";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import { format, parseISO } from "date-fns";
import { DateTime } from "luxon";
import React from "react";
import ReactDatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";

import { CustomLuxonUtils } from "./CustomLuxonUtils";

type DatePickerProps = {
  startDate?: string;
  endDate?: string;
};

function identity(t: string) {
  return t;
}

export const DatePickerDialog: React.FC<DatePickerProps> = ({ startDate, endDate }) => {
  const [state, setState] = React.useState({
    startDate: startDate
      ? parseISO(startDate)
      : new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    endDate: endDate
      ? parseISO(endDate)
      : new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0),
  });
  const handleStartChange = (date: DateTime | null) => {
    if (!date) return;
    setState({ ...state, startDate: date.toJSDate() });
  };
  const handleEndChange = (date: DateTime | null) => {
    if (!date) return;
    setState({ ...state, endDate: date.toJSDate() });
  };
  const { t } = useTranslation();
  return (
    <MuiPickersUtilsProvider utils={CustomLuxonUtils}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <KeyboardDatePicker
            autoOk
            fullWidth
            disableToolbar
            variant="inline"
            inputVariant="outlined"
            format="yyyy/MM/dd"
            name="from"
            label={t("From")}
            value={state.startDate}
            onChange={handleStartChange}
            InputLabelProps={{
              shrink: true,
              style: { marginTop: 0, fontSize: 18 },
            }}
            KeyboardButtonProps={{
              "aria-label": "change date",
            }}
            invalidDateMessage={`${t("Invalid Date Format")}`}
            maxDateMessage={`${t("Date can not be set so far in advance")}`}
            minDateMessage={`${t("To-date must be after from-date")}`}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <KeyboardDatePicker
            autoOk
            fullWidth
            disableToolbar
            variant="inline"
            inputVariant="outlined"
            format="yyyy/MM/dd"
            name="to"
            label={t("To")}
            value={state.endDate}
            onChange={handleEndChange}
            minDate={state.startDate}
            InputLabelProps={{
              shrink: true,
              style: { marginTop: 0, fontSize: 18 },
            }}
            KeyboardButtonProps={{
              "aria-label": "change date",
            }}
            invalidDateMessage={`${t("Invalid Date Format")}`}
            maxDateMessage={`${t("Date can not be set so far in advance")}`}
            minDateMessage={`${t("To-date must be after from-date")}`}
          />
        </Grid>
      </Grid>
    </MuiPickersUtilsProvider>
  );
};

export default class DatePicker extends React.Component {
  state = {
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    endDate: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0),
  };
  options = {
    className: "form-control",
    showMonthDropdown: true,
    showYearDropdown: true,
    dateFormat: "yyyy-MM-dd",
    autoComplete: "off",
    required: true,
  };

  constructor(props: { startDate?: string; endDate?: string }) {
    super(props);
    if (props.startDate && props.endDate) {
      this.state.startDate = parseISO(props.startDate);
      this.state.endDate = parseISO(props.endDate);
    }
  }

  handleStartChange = (e: any) => {
    const date = e.currentTarget.value;
    this.setState({
      startDate: parseISO(date),
    });
  };
  handleEndChange = (e: any) => {
    const date = e.currentTarget.value;
    this.setState({
      endDate: parseISO(date),
    });
  };
  render = () => {
    // Quickfix for illegal hoook use
    //const { t } = useTranslation();
    const t = identity;
    return (
      <>
        <div className="form-row">
          <label htmlFor="from" className="form-label">
            {t("From")}
            <input
              id="from"
              type="date"
              className="form-control"
              value={format(this.state.startDate, "yyyy-MM-dd")}
              onChange={this.handleStartChange}
            />
          </label>
          <label htmlFor="to" className="form-label">
            {t("To")}
            <input
              id="to"
              type="date"
              className="form-control"
              value={format(this.state.endDate, "yyyy-MM-dd")}
              min={format(this.state.startDate, "yyyy-MM-dd")}
              onChange={this.handleEndChange}
            />
          </label>
        </div>
      </>
    );
  };
}

export const DayPicker = (props: { date: Date }) => {
  const minDate = DateTime.now();
  const [selected, setSelected] = React.useState(props.date);
  const handleSelect = (data: Date) => setSelected(data);
  const options = {
    onChange: handleSelect,
    className: "form-control",
    showMonthDropdown: true,
    showYearDropdown: false,
    dateFormat: "yyyy-MM-dd",
    timeFormat: "HH:mm",
    name: "day",
    id: "day",
    autoComplete: "off",
  };
  return (
    <ReactDatePicker {...options} selected={selected} minDate={minDate.toJSDate()} locale="de" />
  );
};

export const BirthDatePicker = (props: any) => {
  const minDate = DateTime.now().minus({ years: 50 });
  const maxDate = DateTime.now().minus({ years: 18 });
  const [selected, setSelected] = React.useState<DateTime>(
    props.defaultValue ? DateTime.fromISO(props.defaultValue) : maxDate
  );
  const handleSelect = (data: Date) => setSelected(DateTime.fromJSDate(data));
  const options = {
    onChange: handleSelect,
    className: "form-control",
    showMonthDropdown: true,
    showYearDropdown: true,
    dateFormat: "yyyy-MM-dd",
    name: "birthday",
    id: "birthday",
    autoComplete: "off",
  };
  return (
    <ReactDatePicker
      {...options}
      selected={selected.toJSDate()}
      minDate={minDate.toJSDate()}
      maxDate={maxDate.toJSDate()}
    />
  );
};

type BlockedDateProps = {
  startDate?: string | null;
  endDate?: string | null;
  name?: string;
};

export const BlockedDatePicker: React.FC<BlockedDateProps> = ({ startDate, endDate, name }) => {
  const [state, setState] = React.useState({
    startDate: startDate ? parseISO(startDate) : new Date(),
    endDate: endDate ? parseISO(endDate) : new Date(),
  });
  const handleStartChange = (date: DateTime | null) => {
    if (!date) return;
    setState({ ...state, startDate: date.toJSDate() });
  };
  const handleEndChange = (date: DateTime | null) => {
    if (!date) return;
    setState({ ...state, endDate: date.toJSDate() });
  };
  const { t } = useTranslation();
  return (
    <MuiPickersUtilsProvider utils={CustomLuxonUtils}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            required
            autoFocus
            fullWidth
            margin="normal"
            type="text"
            color="primary"
            variant="outlined"
            label={t("Name")}
            name="name"
            title=""
            defaultValue={name ? name : ""}
            InputLabelProps={{
              // shrink: true,
              style: { marginTop: 0, fontSize: 18 },
            }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <KeyboardDatePicker
            autoOk
            fullWidth
            disableToolbar
            variant="inline"
            inputVariant="outlined"
            format="yyyy/MM/dd"
            name="from"
            label={t("From")}
            value={state.startDate}
            onChange={handleStartChange}
            InputLabelProps={{
              shrink: true,
              style: { marginTop: 0, fontSize: 18 },
            }}
            KeyboardButtonProps={{
              "aria-label": "change date",
            }}
            invalidDateMessage={`${t("Invalid Date Format")}`}
            maxDateMessage={`${t("Date can not be set so far in advance")}`}
            minDateMessage={`${t("To-date must be after from-date")}`}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <KeyboardDatePicker
            autoOk
            fullWidth
            disableToolbar
            variant="inline"
            inputVariant="outlined"
            format="yyyy/MM/dd"
            name="to"
            label={t("To")}
            value={state.endDate}
            onChange={handleEndChange}
            minDate={state.startDate}
            InputLabelProps={{
              shrink: true,
              style: { marginTop: 0, fontSize: 18 },
            }}
            KeyboardButtonProps={{
              "aria-label": "change date",
            }}
            invalidDateMessage={`${t("Invalid Date Format")}`}
            maxDateMessage={`${t("Date can not be set so far in advance")}`}
            minDateMessage={`${t("To-date must be after from-date")}`}
          />
        </Grid>
      </Grid>
    </MuiPickersUtilsProvider>
  );
};
